import { IconCirclePlus } from '@rossum/ui/icons/tabler';
import { alpha, Box, BoxProps, SvgIcon, useTheme } from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';
import {
  CONTROL_BAR_DISTANCE,
  GRID_LINE_WIDTH,
  INTERACTIVE_AREA_SIZE,
  SEPARATOR_HANDLE_SIZE,
} from './constants';

export type ControlBarProps = Exclude<BoxProps, 'sx'> & {
  orientation: 'horizontal' | 'vertical';
  isInteractive: boolean;
  cssPosition: number | null;
};

type ControlBarHandle = Exclude<BoxProps, 'sx'> & {
  primaryDimValue: number;
  secondaryDimValue: number;
  orientation: 'horizontal' | 'vertical';
};

const ControlBarHandle = ({
  primaryDimValue,
  secondaryDimValue,
  orientation,
}: ControlBarHandle) => {
  const primaryDim = orientation === 'horizontal' ? 'left' : 'top';
  const secondaryDim = orientation === 'horizontal' ? 'top' : 'left';

  return (
    <Box
      style={{
        [primaryDim]: primaryDimValue,
        [secondaryDim]: secondaryDimValue,
      }}
      sx={{
        width: SEPARATOR_HANDLE_SIZE,
        height: SEPARATOR_HANDLE_SIZE,
        backgroundColor: theme => theme.palette.primary.main,
        borderRadius: borderRadius('xl'),
        position: 'absolute',
        pointerEvents: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 12,
        color: theme => theme.palette.common.white,
        zIndex: 101,
      }}
    >
      <SvgIcon color="inherit" fontSize="inherit">
        <IconCirclePlus />
      </SvgIcon>
    </Box>
  );
};

export const ControlBar = ({
  orientation,
  isInteractive,
  cssPosition,
  ...props
}: ControlBarProps) => {
  const theme = useTheme();

  const [primaryDim, secondaryDim, primaryPos, secondaryPos] =
    orientation === 'horizontal'
      ? (['width', 'height', 'top', 'left'] as const)
      : (['height', 'width', 'left', 'top'] as const);

  return (
    <Box
      {...props}
      sx={{
        [primaryDim]: '100%',
        [secondaryDim]: GRID_LINE_WIDTH + 2 * INTERACTIVE_AREA_SIZE,
        position: 'absolute',
        [primaryPos]:
          -GRID_LINE_WIDTH - INTERACTIVE_AREA_SIZE - CONTROL_BAR_DISTANCE,
        [secondaryPos]: 0,
        cursor: 'pointer',
        backgroundColor: alpha(theme.palette.primary.main, 0.5),
        borderColor: 'transparent',
        borderStyle: 'solid',
        borderWidth:
          orientation === 'horizontal'
            ? `${INTERACTIVE_AREA_SIZE}px 0`
            : `0 ${INTERACTIVE_AREA_SIZE}px`,
        backgroundClip: 'content-box',
        transition: theme.transitions.create(
          ['background-color', 'border-color'],
          {
            duration: theme.transitions.duration.short,
          }
        ),
        pointerEvents: isInteractive ? 'auto' : 'none',
        '&:hover': {
          backgroundColor: theme.palette.primary.main,
        },
        ...props.sx,
      }}
    >
      {cssPosition && isInteractive ? (
        <ControlBarHandle
          primaryDimValue={
            cssPosition - SEPARATOR_HANDLE_SIZE / 2 - GRID_LINE_WIDTH / 2
          }
          secondaryDimValue={
            -CONTROL_BAR_DISTANCE +
            SEPARATOR_HANDLE_SIZE / 2 -
            GRID_LINE_WIDTH / 2
          }
          orientation={orientation}
        />
      ) : null}
    </Box>
  );
};
